import type { IntlShape } from 'react-intl';

import {
  OptimizeState,
  PromptModelPublishStatus,
} from '~/api/sylvanus/typedefs.autogen';
import { wordCount } from '~/eds/src/utils/wordCount';
import { Nullable } from '~/types/Nullable';

import { PROMPT_WORD_LIMIT } from '../constants';
import { testIsOptimizingState } from './status';

/**
 * Determine the appropriate model progress status to display:
 * - if optimizing (i.e. 'initializing' | 'running' ), show the optimize state
 * - else if not optimizing (i.e. null | 'done' | 'error')
 *   - if publish state is `null`, show optimize state
 *   - else show the publish state
 * - show publish state in any other case
 */
export const getModelProgressStatusChip = ({
  optimizeState,
  publishStatus,
  docs,
  intl,
}: {
  optimizeState: Nullable<OptimizeState>;
  publishStatus: Nullable<PromptModelPublishStatus>;
  docs: {
    processed: number;
    inScope: number;
    failed: number;
  };
  intl: IntlShape;
}) => {
  if (optimizeState) {
    return testIsOptimizingState(optimizeState) || publishStatus === null
      ? getModelOptimizeStateChip(optimizeState, intl)
      : getModelPublishStatusChip({ publishStatus, docs, intl });
  } else {
    return getModelPublishStatusChip({ publishStatus, docs, intl });
  }
};

const getModelPublishStatusChip = ({
  publishStatus,
  docs,
  intl,
}: {
  publishStatus: Nullable<PromptModelPublishStatus>;
  docs: {
    processed: number;
    inScope: number;
    failed: number;
  };
  intl: IntlShape;
}) => {
  const publishingText = intl.formatMessage({
    id: 'XRayUtils.publishing',
    defaultMessage: 'Publishing',
    description: 'Status text for model in progress',
  });
  const publishingTooltip = intl.formatMessage(
    {
      id: 'XRayUtils.publishingTooltip',
      defaultMessage:
        'Processed {processedDocs} of {docsInScope} {docsInScope, plural, one {document} other {documents}}.',
      description: 'Tooltip text for publishing model',
    },
    {
      processedDocs: docs.processed,
      docsInScope: docs.inScope,
    },
  );
  const retryingTooltip = intl.formatMessage(
    {
      id: 'XRayUtils.publishingFailedTooltip',
      defaultMessage:
        'Processed {processedDocs} of {docsInScope} {docsInScope, plural, one {document} other {documents}}. Retrying {failedDocs} {failedDocs, plural, one {document} other {documents}}.',
      description: 'Tooltip text for publishing model with failed documents',
    },
    {
      processedDocs: docs.processed,
      docsInScope: docs.inScope,
      failedDocs: docs.failed,
    },
  );
  switch (publishStatus) {
    case 'completed':
    case 'failed':
      if (docs.processed === docs.inScope && docs.failed === 0) {
        return {
          status: 'success' as const,
          text: intl.formatMessage({
            id: 'XRayUtils.published',
            defaultMessage: 'Published',
            description: 'Status text for published model',
          }),
          tooltip: publishingTooltip,
        };
      } // else if (docs.processed < docs.inScope || docs.failed > 0) (implicit)
      return {
        status: 'info' as const,
        text: publishingText,
        tooltip: docs.failed > 0 ? retryingTooltip : publishingTooltip,
      };
    case 'in_progress':
    case 'resumed':
      return {
        status: 'info' as const,
        text: publishingText,
        tooltip: docs.failed > 0 ? retryingTooltip : publishingTooltip,
      };
    case 'suspended':
      return {
        status: 'danger' as const,
        text: intl.formatMessage({
          id: 'XRayUtils.suspended',
          defaultMessage: 'Suspended',
          description: 'Status text for suspended model',
        }),
      };
    default:
      return {
        status: 'warning' as const,
        text: intl.formatMessage({
          id: 'XRayUtils.draft',
          defaultMessage: 'Draft',
          description: 'Status text for draft model',
        }),
        tooltip: intl.formatMessage({
          id: 'XRayUtils.draftTooltip',
          defaultMessage: 'Model is being drafted.',
          description: 'Status tooltip for draft model',
        }),
      };
  }
};

export const DEPRECATED_getModelProgressStatusChip = ({
  optimizeState,
  publishStatus,
  intl,
}: {
  optimizeState: Nullable<OptimizeState>;
  publishStatus: Nullable<PromptModelPublishStatus>;
  intl: IntlShape;
}) => {
  if (optimizeState) {
    return testIsOptimizingState(optimizeState) || publishStatus === null
      ? getModelOptimizeStateChip(optimizeState, intl)
      : DEPRECATED_getModelPublishStatusChip(publishStatus, intl);
  } else {
    return DEPRECATED_getModelPublishStatusChip(publishStatus, intl);
  }
};

export const getModelOptimizeStateChip = (
  optimizeState: OptimizeState,
  intl: IntlShape,
) => {
  switch (optimizeState) {
    case 'error':
      return {
        icon: 'ai' as const,
        status: 'danger' as const,
        text: intl.formatMessage({
          id: 'XRayUtils.boostError',
          defaultMessage: 'Boost Error',
          description: 'Status text for boost error state',
        }),
      };
    case 'done':
      return {
        icon: 'ai' as const,
        status: 'ai' as const,
        text: intl.formatMessage({
          id: 'XRayUtils.boostReady',
          defaultMessage: 'Boost Ready',
          description: 'Status text for boost ready state',
        }),
      };
    case 'initializing':
    case 'running':
    default:
      return {
        icon: 'ai' as const,
        status: 'warning' as const,
        text: intl.formatMessage({
          id: 'XRayUtils.boostInProgress',
          defaultMessage: 'Boost in Progress',
          description: 'Status text for boost in progress state',
        }),
      };
  }
};

export const DEPRECATED_getModelPublishStatusChip = (
  publishStatus: Nullable<PromptModelPublishStatus>,
  intl: IntlShape,
) => {
  switch (publishStatus) {
    case 'in_progress':
    case 'resumed':
      return {
        status: 'info' as const,
        text: intl.formatMessage({
          id: 'XRayUtils.inProgress',
          defaultMessage: 'In Progress',
          description: 'Status text for model in progress',
        }),
      };
    case 'completed':
      return {
        status: 'success' as const,
        text: intl.formatMessage({
          id: 'XRayUtils.published',
          defaultMessage: 'Published',
          description: 'Status text for published model',
        }),
      };
    case 'failed':
      return {
        status: 'danger' as const,
        text: intl.formatMessage({
          id: 'XRayUtils.failed',
          defaultMessage: 'Failed',
          description: 'Status text for failed model',
        }),
      };
    case 'suspended':
      return {
        status: 'danger' as const,
        text: intl.formatMessage({
          id: 'XRayUtils.suspended',
          defaultMessage: 'Suspended',
          description: 'Status text for suspended model',
        }),
      };
    default:
      return {
        status: 'warning' as const,
        text: intl.formatMessage({
          id: 'XRayUtils.draft',
          defaultMessage: 'Draft',
          description: 'Status text for draft model',
        }),
      };
  }
};

export const testIsPromptOverLimit = (prompt: string) =>
  wordCount(prompt) > PROMPT_WORD_LIMIT;
